import type { ReactNode } from "react";
import {
  FileExcelOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  MailOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import { RoleAccessBadge } from "@/components/DocumentWorkspace/RoleAccessBadge";
import { DocumentClassBadge } from "@/components/DocumentWorkspace/DocumentClassBadge";
import { DocumentStateBadge } from "@/components/DocumentWorkspace/DocumentStateBadge";
import { getColumnTitle } from "@/utils/tableHelpers";
import { formatFileSize } from "./documentWorkspaceMapping";
import type { StudyDocumentRow } from "./documentWorkspace.types";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    dateStyle: "medium",
  });
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
}

const FILE_ICON_CLASS = "mr-1.5 text-[#6B7280]";

function fileIconFor(row: StudyDocumentRow): ReactNode {
  const extension = row.current_version_original_filename
    ?.split(".")
    .pop()
    ?.toLowerCase();
  const contentType = row.current_version_content_type ?? "";

  if (extension === "pdf" || contentType.includes("pdf")) {
    return <FilePdfOutlined className={FILE_ICON_CLASS} />;
  }
  if (
    extension === "docx" ||
    extension === "doc" ||
    contentType.includes("word")
  ) {
    return <FileWordOutlined className={FILE_ICON_CLASS} />;
  }
  if (
    extension === "xlsx" ||
    extension === "xls" ||
    contentType.includes("sheet") ||
    contentType.includes("excel")
  ) {
    return <FileExcelOutlined className={FILE_ICON_CLASS} />;
  }
  if (extension === "msg" || contentType.includes("message")) {
    return <MailOutlined className={FILE_ICON_CLASS} />;
  }
  return <FileOutlined className={FILE_ICON_CLASS} />;
}

const FIELD_TITLES: Record<string, string> = {
  title: "Document",
  document_class: "Type",
  rollout_scope: "Rollout",
  role_access: "Role Access",
  current_version: "Version",
  uploaded_by_name: "Uploaded By",
  uploaded_at: "Uploaded",
  current_version_state: "Status",
  current_version_amendment_label: "Amendment",
  current_version_file_size_bytes: "Size",
  current_version_approved_on: "Approved",
  current_version_effective_from: "Effective From",
  current_version_effective_to: "Effective To",
  current_version_note: "Note",
  current_version_referencing_records: "Referencing Records",
  created_at: "Created",
  last_modified_at: "Last Updated",
};

function getFieldTitle(field: string): string {
  return FIELD_TITLES[field] || getColumnTitle(field);
}

function getFieldWidth(field: string): number {
  const fixed: Record<string, number> = {
    title: 320,
    document_class: 140,
    role_access: 150,
    current_version: 160,
    current_version_referencing_records: 170,
    current_version_note: 260,
  };
  return fixed[field] ?? Math.max(100, 15 * (getFieldTitle(field).length + 1));
}

function getFieldRenderer(
  field: string,
): (text: any, record: StudyDocumentRow) => ReactNode {
  const renderMap: Record<
    string,
    (text: any, record: StudyDocumentRow) => ReactNode
  > = {
    title: (title: string, record) => (
      <div>
        <span className="font-medium text-[#1F2933]">
          {fileIconFor(record)}
          {title}
        </span>
        <div className="text-[12px] text-[#8AA0A9]">
          {formatFileSize(record.current_version_file_size_bytes)} ·{" "}
          {record.uploaded_by_name}
        </div>
      </div>
    ),
    document_class: (_, record) => (
      <DocumentClassBadge documentClass={record.document_class} />
    ),
    role_access: (_, record) => (
      <RoleAccessBadge roleAccess={record.role_access} />
    ),
    current_version: (_, record) => (
      <span className="inline-flex items-center gap-1.5">
        <span className="font-mono text-[13px] text-[#1F2933]">
          v{record.current_version}
        </span>
        {record.current_version_state === "current" ? null : (
          <DocumentStateBadge state={record.current_version_state} />
        )}
      </span>
    ),
    uploaded_by_name: (value: string) => (
      <AvatarWithInitials
        name={value}
        avatarSize={24}
        avatarFontSize={11}
        labelFontSize={13}
      />
    ),
    uploaded_at: (value: string) => (
      <span className="text-[#374151]">{formatTimestamp(value)}</span>
    ),
    current_version_state: (_, record) => (
      <DocumentStateBadge state={record.current_version_state} />
    ),
    current_version_file_size_bytes: (value: number) => (
      <span className="text-[#374151]">{formatFileSize(value)}</span>
    ),
    current_version_approved_on: (value: string | null) => (
      <span className="text-[#374151]">{formatDate(value)}</span>
    ),
    current_version_effective_from: (value: string | null) => (
      <span className="text-[#374151]">{formatDate(value)}</span>
    ),
    current_version_effective_to: (value: string | null) => (
      <span className="text-[#374151]">{formatDate(value)}</span>
    ),
    current_version_referencing_records: (value: number) =>
      value > 0 ? (
        <span className="text-[#06709B]">{value.toLocaleString()}</span>
      ) : (
        <span className="text-[#8AA0A9]">—</span>
      ),
    created_at: (value: string) => (
      <span className="text-[#374151]">{formatTimestamp(value)}</span>
    ),
    last_modified_at: (value: string) => (
      <span className="text-[#374151]">{formatTimestamp(value)}</span>
    ),
  };

  const defaultRenderer = (text: any) =>
    text === null || text === undefined || text === "" ? (
      <span className="text-[#8AA0A9]">—</span>
    ) : (
      text
    );

  return renderMap[field] || defaultRenderer;
}

/**
 * These carry the current-version's identity for other cells (title, file icon) or aren't
 * meaningful as a standalone column.
 */
const omittedFields = [
  "id",
  "current_version_id",
  "current_version_original_filename",
  "current_version_content_type",
];

export function createDocumentColumns(
  fields: string[],
  lockedColumns: number,
  columnsTitleCreator?: (column: string) => string,
): ColumnsType<StudyDocumentRow> {
  return fields
    .filter((field) => !omittedFields.includes(field))
    .map((field, index) => ({
      title: columnsTitleCreator
        ? columnsTitleCreator(field)
        : getFieldTitle(field),
      dataIndex: field,
      key: field,
      width: getFieldWidth(field),
      fixed: index < lockedColumns ? ("left" as const) : undefined,
      render: (text: any, record: StudyDocumentRow) =>
        getFieldRenderer(field)(text, record),
    }));
}
